/**
 * CONSENT-01 (868kmgf87) — envoltorios finos sobre `apiRequest`, que ya
 * desenvuelve el sobre `{success,data}` y adjunta el CSRF en las mutaciones.
 */

import { ApiRequestError, apiRequest, getSelectedCompanyId } from '@/lib/queryClient';
import type {
  ConsentScope,
  ConsentState,
  ConsentText,
  ConsentType,
  GrantConsentPayload,
  GrantedConsent,
  MemberConsentState,
} from '../types/consent.types';

export const CONSENT_ENDPOINT = '/api/consents';

export function fetchConsentText(consentType: ConsentType, language: string): Promise<ConsentText> {
  return apiRequest<ConsentText>(`${CONSENT_ENDPOINT}/${consentType}/text?language=${language}`);
}

/**
 * Sin alcance, la pregunta es por el de organización; con él, por el de esa
 * propiedad. El servidor no adivina: el alcance viaja en la query.
 */
export function fetchConsentState(consentType: ConsentType, scope?: ConsentScope): Promise<ConsentState> {
  const params = new URLSearchParams();
  if (scope?.propertyId) {
    params.set('propertyId', scope.propertyId);
  }
  if (scope?.companyId) {
    params.set('companyId', scope.companyId);
  }
  const qs = params.toString();
  return apiRequest<ConsentState>(`${CONSENT_ENDPOINT}/${consentType}${qs ? `?${qs}` : ''}`);
}

/**
 * OR-03 (868kmgfd8) B2: devuelve la fila del otorgamiento, no `unknown`.
 *
 * El `id` de esa fila es lo que después viaja al publicar como
 * `attestationConsentId`: sin él no hay forma de decir cuál otorgamiento
 * autoriza la publicación.
 */
export function grantConsent(consentType: ConsentType, payload: GrantConsentPayload): Promise<GrantedConsent> {
  return apiRequest<GrantedConsent>(`${CONSENT_ENDPOINT}/${consentType}/grant`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function revokeConsent(consentType: ConsentType, scope?: ConsentScope): Promise<unknown> {
  // POST y no DELETE: revocar AÑADE una fila que apunta al otorgamiento. Con
  // el alcance del que se otorgó, o la revocación caería en otro.
  return apiRequest(`${CONSENT_ENDPOINT}/${consentType}/revoke`, {
    method: 'POST',
    body: JSON.stringify(scope ? { scope } : {}),
  });
}

/**
 * VOTE-08 CA-06: el estado vigente de OTRA persona en una propiedad, para el
 * detalle del miembro. Sin IP: es lo que el servidor devuelve y nada más.
 */
export function fetchMemberConsent(
  consentType: ConsentType,
  userId: string,
  propertyId: string,
): Promise<MemberConsentState> {
  return apiRequest<MemberConsentState>(
    `${CONSENT_ENDPOINT}/${consentType}/users/${userId}?propertyId=${encodeURIComponent(propertyId)}`,
  );
}

/**
 * VOTE-08 CA-05: el padrón de consentimientos de UNA propiedad, en CSV, como
 * archivo aparte del cómputo. `fetch` + blob porque llega como adjunto y no
 * dentro del sobre JSON, igual que el resto de exportaciones.
 */
export async function downloadConsentExport(propertyId: string, consentType: ConsentType): Promise<void> {
  const headers: Record<string, string> = {};
  const companyId = getSelectedCompanyId();
  if (companyId) {
    headers['X-Company-Id'] = companyId;
  }
  const params = new URLSearchParams({ propertyId, consentType });
  const response = await fetch(`${CONSENT_ENDPOINT}/export?${params.toString()}`, { credentials: 'include', headers });
  if (!response.ok) {
    throw new ApiRequestError(response.status, response.statusText, '');
  }
  const blob = await response.blob();
  const filename = /filename="([^"]+)"/.exec(response.headers.get('Content-Disposition') ?? '')?.[1]
    ?? `consents-${propertyId}.csv`;
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(objectUrl);
}
